"use client"
//packages
import gql from "graphql-tag"
import isUrl from "is-url"
import { PureComponent } from "react"
import { ApolloConsumer, Mutation } from "react-apollo"
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete"
import isEmail from "validator/lib/isEmail"
import ROLE from "../../utils/constant"
import { loadPlaces } from "../../utils/google-maps-loader"

//components
import Checkbox from "../../components/check-box/check-box"
import Cta from "../../components/cta/cta"
import Error from "../../components/error-message/error-message"
import Form from "../../components/form/form"
import Heading from "../../components/heading/heading"
import ImageUpload from "../../components/image-upload/image-upload"
import { CURRENT_USER_QUERY } from "../../components/me/me"
import MultiSelect from "../../components/multi-select/multi-select"
import SelectDropdown from "../../components/select-dropown/select-dropdown"
import LocationItem from "../../components/signup-form/location-item"
import SignUpSect from "../../components/signup-form/sign-up-sect"
import SliderOption from "../../components/slider-option/slider-option"
import TextArea from "../../components/text-area/text-area"
import TextInput from "../../components/text-input/text-input"

import UpgradeDialog from "../../components/upgrade-dialog/upgrade-dialog"
import SuccessCard from "../../components/signup-form/success-card"
import StepperProgress from "../../components/stepper-progress/stepper-progress"
import StepCongratulation from "../../components/signup-steps/stepCongratulation"
import SignupPremium from "../../components/signup-premium/signup-premium"
import StepProfileContent from "../../components/signup-steps/stepProfileContent"
import StepEmailContent from "../../components/signup-steps/stepEmailContent"
import StepFirstContent from "../../components/signup-steps/stepFirstContent"
// styling
import "../../components/signup-form/signup-form.scss"
import "./signup.scss"
// data
import { capabilitiesData } from "../../data/capabilities.json"
import { hoursPerWeekData } from "../../data/hours-per-week.json"
import { ideaStagesData } from "../../data/idea-stages.json"
import { industriesData } from "../../data/industries.json"
import { skillsData } from "../../data/skills.json"
import { startupStagesData } from "../../data/startup-stages.json"

// validation functions
import router from "next/router"
import ImageUploadV2 from "../../components/image-upload/image-upload-v2"
import {
  trackSignUp,
  trackSignupStart,
  trackSignupStep,
} from "../../lib/signup-analytics"
import {
  checkForAmountOfWords,
  isValidName,
  isValidPassword,
} from "../../utils/validation"

const FIND_USER_BY_EMAIL_QUERY = gql`
  query FIND_USER_BY_EMAIL_QUERY($email: String!) {
    userForSignup(email: $email) {
      success
    }
  }
`

const VERIFY_USER_OTP_QUERY = gql`
  query VERIFY_USER_OTP_QUERY($email: String!, $otp: String!) {
    verifyOtpForSignup(email: $email, otp: $otp) {
      success
    }
  }
`
const SEND_OTP_AGAIN_MUTATION = gql`
  mutation SEND_OTP_AGAIN_MUTATION($email: String!) {
    sendVerificationOtpAgain(email: $email) {
      success
    }
  }
`
const SIGNUP_MUTATION = gql`
  mutation SIGNUP_MUTATION(
    $firstName: String!
    $lastName: String!
    $email: String!
    $password: String!
    $role: String!
    $location: String!
    $locationLat: Float!
    $locationLng: Float!
    $hasOwnStartup: Boolean!
    $ownStartupUrl: String
    $lookingToJoinStartupAsFounder: Boolean!
    $hasIdeaForStartup: Boolean!
    $interestedInJobsForStartups: Boolean!
    $whyMakeGoodCofounder: String!
    $elevatorPitchForStartup: String!
    $startupIndustry: Int!
    $startupStage: Int!
    $hoursPerWeekAvailable: Int!
    $ideaTitle: String!
    $ideaStage: Int!
    $ideaIndustry: Int!
    $hasSkills: [Int!]
    $wantsSkills: [Int!]
    $email_subscribedToCommunityAnswers: Boolean!
    $email_subscribedToInternalComms: Boolean!
    $optedInForMarketing: Boolean!
    $agreedToGdpr: Boolean!
    $profilePic: String!
    $imgSizeX: Float!
    $imgSizeY: Float!
    $controlledPositionX: Float!
    $controlledPositionY: Float!
    $companyName: String!
    $shortDescription: String!
    $longDescription: String!
    $capability: Int!
    $otp: String
  ) {
    signUp(
      firstName: $firstName
      lastName: $lastName
      email: $email
      password: $password
      role: $role
      location: $location
      locationLat: $locationLat
      locationLng: $locationLng
      hasOwnStartup: $hasOwnStartup
      ownStartupUrl: $ownStartupUrl
      lookingToJoinStartupAsFounder: $lookingToJoinStartupAsFounder
      hasIdeaForStartup: $hasIdeaForStartup
      interestedInJobsForStartups: $interestedInJobsForStartups
      whyMakeGoodCofounder: $whyMakeGoodCofounder
      elevatorPitchForStartup: $elevatorPitchForStartup
      startupIndustry: $startupIndustry
      startupStage: $startupStage
      hoursPerWeekAvailable: $hoursPerWeekAvailable
      ideaTitle: $ideaTitle
      ideaStage: $ideaStage
      ideaIndustry: $ideaIndustry
      hasSkills: $hasSkills
      wantsSkills: $wantsSkills
      email_subscribedToCommunityAnswers: $email_subscribedToCommunityAnswers
      email_subscribedToInternalComms: $email_subscribedToInternalComms
      optedInForMarketing: $optedInForMarketing
      agreedToGdpr: $agreedToGdpr
      profilePic: $profilePic
      imgSizeX: $imgSizeX
      imgSizeY: $imgSizeY
      controlledPositionX: $controlledPositionX
      controlledPositionY: $controlledPositionY
      companyName: $companyName
      shortDescription: $shortDescription
      longDescription: $longDescription
      capability: $capability
      otp: $otp
    ) {
      id
      firstName
      lastName
      email
      token
    }
  }
`

class Signup extends PureComponent {
  constructor() {
    super()
    this.state = {
      firstName: "",
      firstNameValid: true,
      companyName: "",
      companyNameValid: true,
      lastName: "",
      lastNameValid: true,
      email: "",
      emailValid: true,
      emailUnique: true,
      role: ROLE.cofounder,
      otp: "",
      otpValid: true,
      isOtpVerified: false,
      password: "",
      passwordValid: true,
      location: "",
      whyMakeGoodCofounder: "",
      whyMakeGoodCofounderValid: true,
      elevatorPitchForStartup: "",
      elevatorPitchForStartupValid: true,
      page: 1,
      cityLabel: undefined,
      locationLat: null,
      locationLng: null,
      hasOwnStartup: false,
      ownStartupUrl: "",
      ownStartupUrlValid: true,
      lookingToJoinStartupAsFounder: true,
      hasIdeaForStartup: false,
      interestedInJobsForStartups: true,
      startupIndustry: 0,
      startupIndustryValid: true,
      startupStage: 0,
      startupStageValid: true,
      hoursPerWeekAvailable: 0,
      hoursPerWeekAvailableValid: true,
      ideaTitle: "",
      ideaTitleValid: false,
      ideaStage: 0,
      ideaStageValid: true,
      ideaIndustry: 0,
      ideaIndustryValid: true,
      capability: 0,
      capabilityValid: true,
      hasSkills: [],
      hasSkillsValid: true,
      wantsSkills: [],
      wantsSkillsValid: true,
      email_subscribedToCommunityAnswers: true,
      email_subscribedToInternalComms: true,
      optedInForMarketing: false,
      agreedToGdpr: true,
      agreedToGdprValid: true,
      profilePic: undefined,
      imageErrorMessage: undefined,
      checkingEmailLoading: false,
      checkingOtpLoading: false,
      locationValid: true,
      locationLoading: false,
      profilePicLastModified: null,
      imgSizeX: 0,
      imgSizeY: 0,
      controlledPositionX: 0,
      controlledPositionY: 0,
      ratio: "portrait",
      googleMapAPILoaded: false,
      shortDescription: "",
      shortDescriptionValid: true,
      longDescription: "",
      longDescriptionValid: true,
      isUserBuyingPlan: false,
      bearerToken: "",
      isPlanSelected: false,
    }
  }

  updateValue = ({ forLabel, value }) => {
    const newState = {
      [forLabel]: value,
      [`${forLabel}Valid`]: true,
    }
    // if email reset the email unique value
    if (forLabel === "email") {
      newState.emailUnique = true
    }

    // if toggling any section off then reset the valid values
    if (!value) {
      if (forLabel === "hasOwnStartup") {
        newState.ideaIndustryValid = true
        newState.ideaTitleValid = true
        newState.ideaStageValid = true
        newState.ownStartupUrlValid = true
      }
      if (forLabel === "lookingToJoinStartupAsFounder") {
        newState.whyMakeGoodCofounderValid = true
        if (!this.state.hasIdeaForStartup) {
          newState.hoursPerWeekAvailableValid = true
        }
      }
      if (forLabel === "hasIdeaForStartup") {
        newState.elevatorPitchForStartupValid = true
        newState.ideaTitleValid = true
        newState.ideaStageValid = true
        newState.ideaIndustryValid = true
        if (!this.state.hasIdeaForStartup) {
          newState.hoursPerWeekAvailableValid = true
        }
      }

      if (
        !this.state.hasIdeaForStartup &&
        !this.state.lookingToJoinStartupAsFounder
      ) {
        newState.hoursPerWeekAvailableValid = true
      }
    }

    this.setState(newState)
  }

  // PlacesAutocomplete throws on mount until the Places library is in, and
  // load() alone can resolve before it is (see loadPlaces)
  loadGoogleMaps = () => {
    loadPlaces().then((ok) => {
      if (ok) this.setState({ googleMapAPILoaded: true })
    })
  }

  componentDidMount() {
    this.loadGoogleMaps()
    trackSignupStart("page")
  }

  componentWillUnmount() {
    clearTimeout(this.googleMapsTimeout)
  }

  toggleSelection = ({ forLabel, id }) => {
    const copiedState = [...this.state[forLabel]]
    const index = copiedState.indexOf(id)

    if (index > -1) {
      copiedState.splice(index, 1)
    } else {
      copiedState.push(id)
    }

    this.setState({
      [forLabel]: copiedState,
      [`${forLabel}Valid`]: true,
    })
  }

  checkUserEmail = (client) => {
    const { email } = this.state
    return client
      .query({
        query: FIND_USER_BY_EMAIL_QUERY,
        variables: { email },
      })
      .then(
        ({
          data: {
            userForSignup: { success },
          },
        }) => {
          let emailUnique = false
          if (success) {
            emailUnique = true
          }
          const companyNameValid =
            this.state.role === ROLE.vendor
              ? isValidName(this.state.companyName)
              : true
          const firstNameValid = isValidName(this.state.firstName)
          const lastNameValid =
            this.state.role === ROLE.vendor
              ? true
              : isValidName(this.state.lastName)
          const emailValid = isEmail(this.state.email.trim())
          const passwordValid = isValidPassword(this.state.password)
          const agreedToGdprValid = this.state.agreedToGdpr

          return this.setState(
            {
              firstNameValid,
              companyNameValid,
              lastNameValid,
              emailValid,
              passwordValid,
              agreedToGdprValid,
              emailUnique: emailValid ? emailUnique : true,
              checkingEmailLoading: false,
            },
            () => {
              if (
                firstNameValid &&
                companyNameValid &&
                lastNameValid &&
                emailValid &&
                passwordValid &&
                agreedToGdprValid &&
                emailUnique
              ) {
                return this.updatePage()
              }
            }
          )
        }
      )
      .catch((err) => console.log(err))
  }

  verifyOtp = (client) => {
    const { email, otp } = this.state
    return client
      .query({
        query: VERIFY_USER_OTP_QUERY,
        variables: { email, otp },
      })
      .then(
        ({
          data: {
            verifyOtpForSignup: { success },
          },
        }) => {
          if (success) {
            this.setState(
              {
                isOtpVerified: true,
              },
              () => {
                if (this.state.isOtpVerified) {
                  return this.updatePage()
                }
              }
            )
          } else {
            this.setState({
              checkingOtpLoading: false,
              otpValid: false,
            })
          }
        }
      )
      .catch((err) => console.log(err))
  }

  sendOtpAgain = (client) => {
    const { email } = this.state
    this.setState({ checkingOtpLoading: true })
    setTimeout(() => {
      this.setState({ checkingOtpLoading: false })
    }, 3000)
    return client
      .mutate({
        mutation: SEND_OTP_AGAIN_MUTATION,
        variables: { email },
      })
      .then(
        ({
          data: {
            sendVerificationOtpAgain: { success },
          },
        }) => {
          if (success) {
            console.log("otp send successfully")
          }
        }
      )
  }

  pageValidationChecks = (page, client, signUp) => {
    const {
      hasIdeaForStartup,
      hasOwnStartup,
      startupIndustry,
      startupStage,
      lookingToJoinStartupAsFounder,
      whyMakeGoodCofounder,
      elevatorPitchForStartup,
      ideaTitle,
      ideaStage,
      profilePic,
      ownStartupUrl,
      ideaIndustry,
      capability,
      hoursPerWeekAvailable,
      interestedInJobsForStartups,
      hasSkills,
      wantsSkills,
      location,
      locationLat,
      locationLng,
    } = this.state

    if (this.state.role === ROLE.cofounder) {
      switch (page) {
        case 1:
          this.setState(
            {
              checkingEmailLoading: true,
            },
            () => this.checkUserEmail(client)
          ) // this function also checks first last name etc
          break
        case 2:
          this.setState(
            {
              checkingOtpLoading: true,
            },
            () => this.verifyOtp(client)
          )
          break
        case 3:
          // checks for already has a startup
          const startupIndustryValid =
            (hasOwnStartup && startupIndustry !== 0) || !hasOwnStartup
              ? true
              : false

          const ownStartupUrlValid =
            (hasOwnStartup && isUrl(ownStartupUrl.trim())) || !hasOwnStartup
              ? true
              : false

          const startupStageValid =
            (hasOwnStartup && startupStage !== 0) || !hasOwnStartup
              ? true
              : false

          // check why make good co-founder blurb for either if has an idea or wants to join as cofounder
          const whyMakeGoodCofounderValid =
            (hasIdeaForStartup &&
              checkForAmountOfWords(whyMakeGoodCofounder, 20)) ||
            (lookingToJoinStartupAsFounder &&
              checkForAmountOfWords(whyMakeGoodCofounder, 20)) ||
            (!lookingToJoinStartupAsFounder && !hasIdeaForStartup)
              ? true
              : false

          // checks for has an idea
          const elevatorPitchForStartupValid =
            (hasIdeaForStartup &&
              checkForAmountOfWords(elevatorPitchForStartup, 20)) ||
            !hasIdeaForStartup
              ? true
              : false

          const ideaStageValid =
            (hasIdeaForStartup && ideaStage !== 0) || !hasIdeaForStartup
              ? true
              : false

          const ideaIndustryValid =
            (hasIdeaForStartup && ideaIndustry !== 0) || !hasIdeaForStartup
              ? true
              : false

          // if they have idea and hpwa valid
          // if they looking to join and hpwa valid
          // if BOTH idea and looking are false then return true

          const hoursPerWeekAvailableValid =
            (hasIdeaForStartup && hoursPerWeekAvailable !== 0) ||
            (lookingToJoinStartupAsFounder && hoursPerWeekAvailable !== 0) ||
            (!lookingToJoinStartupAsFounder && !hasIdeaForStartup)
              ? true
              : false

          const hasSkillsValid =
            (hasIdeaForStartup && hasSkills.length > 0) ||
            (lookingToJoinStartupAsFounder && hasSkills.length > 0) ||
            (!lookingToJoinStartupAsFounder && !hasIdeaForStartup)
              ? true
              : false

          const wantsSkillsValid =
            (hasIdeaForStartup && wantsSkills.length > 0) ||
            (lookingToJoinStartupAsFounder && wantsSkills.length > 0) ||
            (!lookingToJoinStartupAsFounder && !hasIdeaForStartup)
              ? true
              : false

          const locationValid =
            location.length > 0 && locationLat && locationLng

          if (!profilePic) {
            return this.setState({
              imageErrorMessage: "Please select a profile picture",
            })
          }

          this.setState(
            {
              whyMakeGoodCofounderValid,
              elevatorPitchForStartupValid,
              ownStartupUrlValid,
              startupIndustryValid,
              startupStageValid,
              ideaStageValid,
              ideaIndustryValid,
              hoursPerWeekAvailableValid,
              hasSkillsValid,
              wantsSkillsValid,
              locationValid,
            },
            () => {
              if (
                whyMakeGoodCofounderValid &&
                elevatorPitchForStartupValid &&
                startupIndustryValid &&
                startupStageValid &&
                ideaStageValid &&
                ownStartupUrlValid &&
                ideaIndustryValid &&
                hoursPerWeekAvailableValid &&
                hasSkillsValid &&
                wantsSkillsValid &&
                locationValid
              ) {
                signUp()
                  .then(() => {
                    trackSignUp("page", ROLE.cofounder)
                    this.updatePage()
                  })
                  .catch((e) => {
                    console.log(e)
                  })
              }
            }
          )
          break
        case 4:
          break
        case 5:
          break
        default:
          return
      }
    }

    if (this.state.role === ROLE.vendor) {
      switch (page) {
        case 1:
          this.setState(
            {
              checkingEmailLoading: true,
            },
            () => this.checkUserEmail(client)
          )
          break
        case 2:
          this.setState(
            {
              checkingOtpLoading: true,
            },
            () => this.verifyOtp(client)
          )
          break
        case 3:
          this.updatePage()
          break
        case 4:
          const shortDescriptionValid = checkForAmountOfWords(
            this.state.shortDescription,
            20
          )
          const longDescriptionValid = checkForAmountOfWords(
            this.state.longDescription,
            50
          )
          const ideaIndustryValid = ideaIndustry !== 0 ? true : false
          const capabilityValid = capability !== 0 ? true : false
          this.setState(
            {
              longDescriptionValid,
              shortDescriptionValid,
              ideaIndustryValid,
              capabilityValid,
            },
            () => {
              if (
                longDescriptionValid &&
                shortDescriptionValid &&
                ideaIndustryValid &&
                capabilityValid
              ) {
                return this.updatePage()
              }
            }
          )
          break
        case 5:
          const locationValid =
            location.length > 0 && locationLat && locationLng

          if (!profilePic) {
            return this.setState({
              imageErrorMessage: "Please select a profile picture",
            })
          }

          this.setState(
            {
              locationValid,
            },
            () => {
              if (locationValid) {
                signUp()
                  .then(() => {
                    trackSignUp("page", ROLE.vendor)
                    // this page has no overlay to close (that's the modal);
                    // calling toggleOverlay here threw and stranded vendors
                    router.push("/")
                  })
                  .catch((e) => console.log(e))
              }
            }
          )
          break
        default:
          return
      }
    }
  }

  updatePage = () => {
    trackSignupStep("page", this.state.role, this.props.page)
    this.props.updatePage()
  }

  setPlanSelected = () => {
    this.setState({ isPlanSelected: true })
  }

  handleLocationChange = (location) => {
    this.setState({ location, locationValid: true })
  }

  removeSelectedCity = () => {
    this.setState({
      cityLabel: undefined,
      location: "",
      locationLat: null,
      locationLng: null,
    })
  }

  handleLocationSelect = (location, placeId) => {
    if (!placeId) return
    if (location.length === 0) return
    this.setState(
      {
        locationLoading: true,
      },
      () => {
        geocodeByAddress(location)
          .then((results) => getLatLng(results[0]))
          .then((latLng) => {
            return this.setState({
              cityLabel: location,
              locationLat: latLng.lat,
              locationLng: latLng.lng,
              locationLoading: false,
            })
          })
          .catch((error) => console.error("Error", error))
      }
    )
  }

  onWebsiteUrlBlur = (val) => {
    if (val.length > 0) {
      if (isUrl(val.trim())) {
        this.setState({
          ownStartupUrlValid: true,
        })
      } else {
        this.setState({
          ownStartupUrlValid: false,
        })
      }
    } else {
      this.setState({
        ownStartupUrlValid: true,
      })
    }
  }

  setCookie = (resp, client) => {
    if (resp.signUp.token) {
      this.setState({ bearerToken: resp.signUp.token })
      const token = resp.signUp.token
      localStorage.setItem("token", token)
      fetch(`/authcallback?token=${token}`).then(async () => {
        client.resetStore()
      })
    }
  }

  updateImage = (params) => {
    console.log({ params })
    this.setState({
      ...params,
    })
  }

  handleStepChange = (step) => {
    this.setState({ page: step })
  }

  renderLeftStepContent = () => {
    switch (this.props.page) {
      case 1:
        return <StepFirstContent />
      // return <StepProfileContent />;
      case 2:
        return <StepEmailContent />
      case 3:
        return <StepProfileContent />
      case 4:
        return <SignupPremium />
      default:
        return <StepCongratulation />
    }
  }

  render() {
    const { query, me } = this.props
    console.log({ signup: this.props })

    const {
      firstName,
      firstNameValid,
      lastName,
      lastNameValid,
      email,
      emailValid,
      emailUnique,
      otp,
      otpValid,
      password,
      passwordValid,
      role,
      location,
      // page,
      cityLabel,
      locationLat,
      locationLng,
      hasOwnStartup,
      ownStartupUrl,
      ownStartupUrlValid,
      lookingToJoinStartupAsFounder,
      hasIdeaForStartup,
      interestedInJobsForStartups,
      startupIndustry,
      startupStage,
      whyMakeGoodCofounder,
      elevatorPitchForStartup,
      ideaStage,
      ideaIndustry,
      capability,
      capabilityValid,
      hoursPerWeekAvailable,
      hasSkills,
      wantsSkills,
      email_subscribedToCommunityAnswers,
      email_subscribedToInternalComms,
      optedInForMarketing,
      agreedToGdpr,
      agreedToGdprValid,
      profilePic,
      imageErrorMessage,
      checkingEmailLoading,
      checkingOtpLoading,
      whyMakeGoodCofounderValid,
      elevatorPitchForStartupValid,
      startupIndustryValid,
      startupStageValid,
      ideaTitle,
      ideaTitleValid,
      ideaStageValid,
      ideaIndustryValid,
      hoursPerWeekAvailableValid,
      hasSkillsValid,
      wantsSkillsValid,
      locationValid,
      locationLoading,
      profilePicLastModified,
      imgSizeX,
      imgSizeY,
      controlledPositionX,
      controlledPositionY,
      ratio,
      googleMapAPILoaded,
      companyName,
      companyNameValid,
      shortDescription,
      shortDescriptionValid,
      longDescription,
      longDescriptionValid,
      bearerToken,
      isPlanSelected,
    } = this.state

    const { page } = this.props

    if (me && page == 1) {
      // Phase 3: post-signup redirect goes to FounderAssist instead of
      // /profile. The questionnaire is pre-filled from signup data
      // (Phase 4), so the user can hit "Generate my plan" right away and
      // see real product value in ~30 seconds — the first-value moment
      // that didn't exist before.
      router.push("/founder-assist")
    }
    return (
      <div className="signup__section__full">
        <div className="signup__flex__section">
          <div className="signup__left__section">
            {this.renderLeftStepContent(page)}
          </div>
          <div className="signup__right__section">
            <div className="heading__section">
              {(page === 1 || page === 2 || page === 3) && (
                <Heading copy="Join the Community" element="h2" />
              )}
              {page === 4 && !isPlanSelected && (
                <Heading copy="You are in! Want More?" element="h2" />
              )}

              {page === 4 && isPlanSelected && (
                <Heading copy="Great! Lets Get You Upgraded" element="h2" />
              )}

              {page === 5 && <Heading copy="Congratulations!" element="h2" />}
            </div>
            <div className="signup__form__card">
              <div>
                <div>
                  {/* stepper component */}
                  <StepperProgress currentStep={page}></StepperProgress>

                  {/* form component */}
                  <div className="step-content">
                    <ApolloConsumer>
                      {(client) => (
                        <Mutation
                          mutation={SIGNUP_MUTATION}
                          refetchQueries={[{ query: CURRENT_USER_QUERY }]}
                          onCompleted={(resp) => {
                            this.setCookie(resp, client)
                          }}
                          awaitRefetchQueries
                          // update={this.updatePage}
                          variables={{
                            firstName,
                            lastName,
                            email,
                            password,
                            role,
                            hasOwnStartup,
                            ownStartupUrl,
                            lookingToJoinStartupAsFounder,
                            hasIdeaForStartup,
                            interestedInJobsForStartups,
                            startupIndustry,
                            startupStage,
                            whyMakeGoodCofounder: whyMakeGoodCofounder.replace(
                              /\n/g,
                              "<br />"
                            ),
                            elevatorPitchForStartup:
                              elevatorPitchForStartup.replace(/\n/g, "<br />"),
                            ideaStage,
                            ideaTitle,
                            ideaIndustry,
                            hoursPerWeekAvailable,
                            location: cityLabel,
                            locationLat,
                            locationLng,
                            hasSkills,
                            wantsSkills,
                            email_subscribedToCommunityAnswers,
                            email_subscribedToInternalComms,
                            optedInForMarketing,
                            agreedToGdpr,
                            profilePic,
                            imgSizeX,
                            imgSizeY,
                            controlledPositionX,
                            controlledPositionY,
                            companyName,
                            shortDescription,
                            longDescription,
                            capability,
                            otp,
                          }}
                        >
                          {(signUp, { error, loading }) => {
                            return this.state.role === ROLE.cofounder ? (
                              <>
                                {page === 1 && role === ROLE.cofounder && (
                                  <ApolloConsumer>
                                    {(client) => (
                                      <Form
                                        onSubmit={() =>
                                          this.pageValidationChecks(
                                            page,
                                            client
                                          )
                                        }
                                        method="post"
                                        loading={checkingEmailLoading}
                                        autoCompleteDisabled
                                      >
                                        <Error error={error} />
                                        <SignUpSect>
                                          <div className="heading_section"></div>

                                          <div className="flex__section__form">
                                            <div className="input__section__fixed">
                                              <Error error={error} />
                                              <TextInput
                                                label="First Name"
                                                forLabel="firstName"
                                                type="text"
                                                updateValue={this.updateValue}
                                                value={firstName}
                                                isValid={firstNameValid}
                                                validationErrorMsg="Please provide a valid first name using English characters"
                                              />{" "}
                                            </div>
                                            {/* <a
                                              className="header_a_button signup__vendor__button header_a_button_signup_text"
                                              // onClick={() => this.setState({ role: ROLE.vendor })}
                                            >
                                              Company? Sign Up as a <br></br>
                                              Vendor/Resource Instead
                                            </a> */}
                                          </div>
                                          <div className="input__section__fixed">
                                            <TextInput
                                              label="Last Name"
                                              forLabel="lastName"
                                              type="text"
                                              updateValue={this.updateValue}
                                              value={lastName}
                                              isValid={lastNameValid}
                                              validationErrorMsg="Please provide a valid last name using English characters"
                                            />
                                          </div>
                                          <div>
                                            <TextInput
                                              label="Email"
                                              forLabel="email"
                                              type="text"
                                              updateValue={this.updateValue}
                                              value={email}
                                              isValid={emailValid}
                                              validationErrorMsg="Please provide a valid email"
                                              emailUnique={emailUnique}
                                            />
                                          </div>
                                          <div className="input__section__fixed">
                                            <TextInput
                                              label="Password"
                                              forLabel="password"
                                              type="password"
                                              updateValue={this.updateValue}
                                              value={password}
                                              isValid={passwordValid}
                                              validationErrorMsg="Please provide a password with a minimum length of 8 characters, at least one letter and at least one number"
                                            />
                                          </div>
                                          <div className="btn__section__signup__form">
                                            <Cta
                                              tag="button"
                                              type="submit"
                                              text="Next -> Validate your email"
                                            />
                                          </div>
                                          <div className="policy__section">
                                            <Checkbox
                                              label="By clicking this button you agree to the Privacy Policy and Terms of Service"
                                              supportedLinks={[
                                                {
                                                  supportedLinkText:
                                                    "Read the Privacy Policy",
                                                  supportedLink:
                                                    "/privacy-policy",
                                                },
                                                {
                                                  supportedLinkText:
                                                    "Read the Terms of Service",
                                                  supportedLink:
                                                    "/terms-of-service",
                                                },
                                              ]}
                                              forLabel="agreedToGdpr"
                                              isSelected={agreedToGdpr}
                                              onClick={this.updateValue}
                                              hasValidation
                                              isValid={agreedToGdprValid}
                                              validationErrorMsg="Please accept our privacy policy and terms of service"
                                            />
                                          </div>
                                        </SignUpSect>
                                      </Form>
                                    )}
                                  </ApolloConsumer>
                                )}
                                {page === 2 && role === ROLE.cofounder && (
                                  <ApolloConsumer>
                                    {(client) => (
                                      <Form
                                        onSubmit={() =>
                                          this.pageValidationChecks(
                                            page,
                                            client
                                          )
                                        }
                                        method="post"
                                        loading={checkingOtpLoading}
                                        autoCompleteDisabled
                                      >
                                        <Error error={error} />
                                        <SignUpSect>
                                          <div className="otp_screen_icon">
                                            <img
                                              src="/static/images/mailIcon.svg"
                                              alt="StartHawk"
                                            />
                                          </div>
                                          <div className="otp_mail_text">
                                            <p>
                                              A code has been sent to your email
                                              address:
                                              <span className="email_text">
                                                {" "}
                                                {email}
                                              </span>{" "}
                                              <br></br> Please enter that code
                                              here
                                            </p>
                                          </div>
                                          <Error error={error} />{" "}
                                          <div className="otp_screen_input">
                                            <TextInput
                                              label="Enter the 6-digit code here"
                                              forLabel="otp"
                                              type="text"
                                              updateValue={this.updateValue}
                                              value={otp}
                                              isValid={otpValid}
                                              maxLength={6}
                                              validationErrorMsg="incorrect Otp or enter valid otp"
                                            />
                                          </div>
                                          <div className="otp_section_note">
                                            <p>
                                              Didn’t receive the email?
                                              <span
                                                onClick={() =>
                                                  this.sendOtpAgain(client)
                                                }
                                                className="try_again_text"
                                              >
                                                &nbsp;Try Again
                                              </span>
                                              <br></br>
                                              Check spam/junk folders if you do
                                              not see the email
                                            </p>
                                          </div>
                                          <Cta
                                            tag="button"
                                            type="submit"
                                            text="Next -> Add Your Profile Details"
                                          />
                                        </SignUpSect>
                                      </Form>
                                    )}
                                  </ApolloConsumer>
                                )}
                                {page === 3 && role === ROLE.cofounder && (
                                  <SignUpSect>
                                    {googleMapAPILoaded && (
                                      <PlacesAutocomplete
                                        value={location}
                                        searchOptions={{ types: ["(cities)"] }}
                                        onChange={this.handleLocationChange}
                                        onSelect={this.handleLocationSelect}
                                        highlightFirstSuggestion
                                      >
                                        {({
                                          getInputProps,
                                          suggestions,
                                          getSuggestionItemProps,
                                          placesLoading,
                                        }) => (
                                          <div className="profile__details">
                                            <Form
                                              onSubmit={() =>
                                                this.pageValidationChecks(
                                                  3,
                                                  null,
                                                  signUp
                                                )
                                              }
                                              method="post"
                                              loading={
                                                locationLoading || loading
                                              }
                                              autoCompleteDisabled
                                            >
                                              <SliderOption
                                                active={hasIdeaForStartup}
                                                forLabel="hasIdeaForStartup"
                                                onClick={this.updateValue}
                                                headingElement="h3"
                                                label="I have an idea for a startup and i'm looking for a co-founder"
                                              />

                                              {!lookingToJoinStartupAsFounder &&
                                                hasIdeaForStartup && (
                                                  <>
                                                    <TextArea
                                                      value={
                                                        whyMakeGoodCofounder
                                                      }
                                                      forLabel="whyMakeGoodCofounder"
                                                      updateValue={
                                                        this.updateValue
                                                      }
                                                      placeholder="Tell us why you think you'd make a good co-founder..."
                                                      isValid={
                                                        whyMakeGoodCofounderValid
                                                      }
                                                      validationErrorMsg="Please tell us in 20 words or more why you think you would make a good co-founder (4000 character max)"
                                                      minWords={20}
                                                      showWordCounter={true}
                                                      showPreFilledOption={true}
                                                      preFilledContent={
                                                        "I am still working out the details to fill out this section.  Reach out or check back later.  Thanks for your interest in my profile!"
                                                      }
                                                    />
                                                    <SelectDropdown
                                                      options={hoursPerWeekData}
                                                      label="How many hours per week are you available to work on a project"
                                                      forLabel="hoursPerWeekAvailable"
                                                      selectedOptionId={
                                                        hoursPerWeekAvailable
                                                      }
                                                      onClick={this.updateValue}
                                                      isValid={
                                                        hoursPerWeekAvailableValid
                                                      }
                                                      validationErrorMsg="Please select how many hours per week you could contribute to a project"
                                                      resetDefaultState
                                                    />
                                                  </>
                                                )}
                                              {hasIdeaForStartup && (
                                                <>
                                                  <TextInput
                                                    label="Idea Title"
                                                    forLabel="ideaTitle"
                                                    type="text"
                                                    updateValue={
                                                      this.updateValue
                                                    }
                                                    onBlur={this.ideaTitle}
                                                    value={ideaTitle}
                                                    isValid={ideaTitleValid}
                                                  />
                                                  <TextArea
                                                    value={
                                                      elevatorPitchForStartup
                                                    }
                                                    forLabel="elevatorPitchForStartup"
                                                    updateValue={
                                                      this.updateValue
                                                    }
                                                    placeholder="Give us an elevator pitch about your idea. It's also good to highlight what you're after in a co-founder"
                                                    isValid={
                                                      elevatorPitchForStartupValid
                                                    }
                                                    validationErrorMsg="Please tell us about your idea in 20 words or more (4000 character max)"
                                                    minWords={20}
                                                    showWordCounter={true}
                                                    showPreFilledOption={true}
                                                    preFilledContent={
                                                      "I am still working out the details to fill out this section.  Reach out or check back later.  Thanks for your interest in my profile!"
                                                    }
                                                  />
                                                  <SelectDropdown
                                                    options={ideaStagesData}
                                                    label="What stage is your idea at?"
                                                    forLabel="ideaStage"
                                                    selectedOptionId={ideaStage}
                                                    onClick={this.updateValue}
                                                    isValid={ideaStageValid}
                                                    validationErrorMsg="Please select a stage for your idea"
                                                    resetDefaultState
                                                  />
                                                  <SelectDropdown
                                                    options={industriesData}
                                                    label="Which industry/ business type best describes your idea?"
                                                    forLabel="ideaIndustry"
                                                    selectedOptionId={
                                                      ideaIndustry
                                                    }
                                                    onClick={this.updateValue}
                                                    isValid={ideaIndustryValid}
                                                    validationErrorMsg="Please select an industry/ business type for your idea"
                                                    resetDefaultState
                                                  />
                                                </>
                                              )}
                                              <SliderOption
                                                active={
                                                  lookingToJoinStartupAsFounder
                                                }
                                                forLabel="lookingToJoinStartupAsFounder"
                                                onClick={this.updateValue}
                                                headingElement="h3"
                                                label="I am looking to partner up on someone else's idea as a co-founder"
                                              />
                                              {lookingToJoinStartupAsFounder && (
                                                <>
                                                  <TextArea
                                                    value={whyMakeGoodCofounder}
                                                    forLabel="whyMakeGoodCofounder"
                                                    updateValue={
                                                      this.updateValue
                                                    }
                                                    placeholder="Tell us why you think you'd make a good co-founder..."
                                                    isValid={
                                                      whyMakeGoodCofounderValid
                                                    }
                                                    validationErrorMsg="Please tell us in 20 words or more why you think you would make a good co-founder (4000 character max)"
                                                    minWords={20}
                                                    showWordCounter={true}
                                                    showPreFilledOption={true}
                                                    preFilledContent={
                                                      "I am still working out the details to fill out this section.  Reach out or check back later.  Thanks for your interest in my profile!"
                                                    }
                                                  />
                                                  <div className="space">
                                                    <SelectDropdown
                                                      options={hoursPerWeekData}
                                                      label="How many hours per week are you available to work on a project"
                                                      forLabel="hoursPerWeekAvailable"
                                                      selectedOptionId={
                                                        hoursPerWeekAvailable
                                                      }
                                                      onClick={this.updateValue}
                                                      isValid={
                                                        hoursPerWeekAvailableValid
                                                      }
                                                      validationErrorMsg="Please select how many hours per week you could contribute to a project"
                                                      resetDefaultState
                                                    />
                                                  </div>
                                                </>
                                              )}
                                              <SliderOption
                                                active={hasOwnStartup}
                                                forLabel="hasOwnStartup"
                                                onClick={this.updateValue}
                                                headingElement="h3"
                                                label="I already have my own successful startup"
                                              />
                                              {hasOwnStartup && (
                                                <>
                                                  <SelectDropdown
                                                    options={industriesData}
                                                    label="Which industry/ business type best describes your business?"
                                                    forLabel="startupIndustry"
                                                    selectedOptionId={
                                                      startupIndustry
                                                    }
                                                    onClick={this.updateValue}
                                                    isValid={
                                                      startupIndustryValid
                                                    }
                                                    validationErrorMsg="Please select an industry/ business type that best describes your business"
                                                    resetDefaultState
                                                  />
                                                  <SelectDropdown
                                                    options={startupStagesData}
                                                    label="Which stage would you say your startup is in?"
                                                    forLabel="startupStage"
                                                    selectedOptionId={
                                                      startupStage
                                                    }
                                                    onClick={this.updateValue}
                                                    isValid={startupStageValid}
                                                    validationErrorMsg="Please select a stage for your startup"
                                                    resetDefaultState
                                                  />
                                                  <TextInput
                                                    label="Website Link"
                                                    forLabel="ownStartupUrl"
                                                    type="text"
                                                    updateValue={
                                                      this.updateValue
                                                    }
                                                    onBlur={
                                                      this.onWebsiteUrlBlur
                                                    }
                                                    value={ownStartupUrl}
                                                    isValid={ownStartupUrlValid}
                                                    validationErrorMsg="Please provide a valid url for your startup. It should look something like http://yourwebsite.com, https://yourwebsite.com, http://www.yourwebsite.com or https://www.yourwebsite.com"
                                                  />
                                                </>
                                              )}
                                              <SliderOption
                                                active={
                                                  interestedInJobsForStartups
                                                }
                                                forLabel="interestedInJobsForStartups"
                                                onClick={this.updateValue}
                                                headingElement="h3"
                                                label="I am interested in jobs in startups"
                                              />

                                              {/* <h2>
                                                Please tell us a little more
                                                info
                                              </h2> */}
                                              <Error error={error} />
                                              <div className="mt-16">
                                                <TextInput
                                                  label="City/ town where you're based"
                                                  forLabel="location"
                                                  type="text"
                                                  updateValue={this.updateValue}
                                                  modifierClass="location"
                                                  additionalInputProps={getInputProps()}
                                                  hasSpinner
                                                  loading={placesLoading}
                                                  value={location}
                                                  selectedOption={cityLabel}
                                                  removeSelectedOption={
                                                    this.removeSelectedCity
                                                  }
                                                  isValid={locationValid}
                                                  validationErrorMsg="Please tell us where you're based (select an option from the options that appear after typing)"
                                                />
                                              </div>
                                              <div className="location-auto">
                                                {suggestions.length > 0 && (
                                                  <div className="location-auto__inner">
                                                    {suggestions.map(
                                                      (suggestion) => {
                                                        return (
                                                          <LocationItem
                                                            key={
                                                              suggestion.place_id ||
                                                              suggestion.placeId
                                                            }
                                                            getSuggestionItemProps={
                                                              getSuggestionItemProps
                                                            }
                                                            className={
                                                              suggestion.active
                                                                ? "location-auto__suggestion location-auto__suggestion--active"
                                                                : "location-auto__suggestion"
                                                            }
                                                            suggestion={
                                                              suggestion
                                                            }
                                                          />
                                                        )
                                                      }
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              {(hasIdeaForStartup ||
                                                lookingToJoinStartupAsFounder) && (
                                                <>
                                                  <MultiSelect
                                                    options={skillsData}
                                                    selectedOptions={hasSkills}
                                                    label="Your Skills:"
                                                    selectionMax={2}
                                                    forLabel="hasSkills"
                                                    toggleSelection={
                                                      this.toggleSelection
                                                    }
                                                    component="li"
                                                    isValid={hasSkillsValid}
                                                    validationErrorMsg="Please tell us up to 2 skills you bring to the table as a co-founder"
                                                  />
                                                  <MultiSelect
                                                    options={skillsData}
                                                    selectedOptions={
                                                      wantsSkills
                                                    }
                                                    label="I am looking for the following skills"
                                                    selectionMax={2}
                                                    forLabel="wantsSkills"
                                                    toggleSelection={
                                                      this.toggleSelection
                                                    }
                                                    component="li"
                                                    isValid={wantsSkillsValid}
                                                    validationErrorMsg="Please tell us up to 2 skills you would like to see in a co-founder"
                                                  />
                                                </>
                                              )}
                                              <Heading
                                                copy="Profile Picture:"
                                                element="h3"
                                              />
                                              <ImageUploadV2
                                                imageErrorMessage={
                                                  imageErrorMessage
                                                }
                                                profilePic={profilePic}
                                                profilePicLastModified={
                                                  profilePicLastModified
                                                }
                                                updateImage={this.updateImage}
                                                imgSizeX={imgSizeX}
                                                imgSizeY={imgSizeY}
                                                controlledPositionX={
                                                  controlledPositionX
                                                }
                                                controlledPositionY={
                                                  controlledPositionY
                                                }
                                                ratio={ratio}
                                              />
                                              <div className="heading">
                                                <h3>
                                                  Communication Preferences
                                                </h3>
                                              </div>
                                              {/* <div className="signup_screen_text">
                                                <p>
                                                  Communication is pretty
                                                  important for our network to
                                                  operate. The easier we
                                                  communicate, the easier
                                                  co-founder connections can be
                                                  facilitated.
                                                </p>
                                              </div> */}
                                              <div className="list_checkbox">
                                                <Checkbox
                                                  label="Send me an email when someone messages me on FounderCloud"
                                                  forLabel="email_subscribedToCommunityAnswers"
                                                  isSelected={
                                                    email_subscribedToCommunityAnswers
                                                  }
                                                  onClick={this.updateValue}
                                                  isValid={true}
                                                />
                                                <Checkbox
                                                  label="I would like to receive the weekly FounderCloud update newsletter with news and community activity information"
                                                  forLabel="email_subscribedToInternalComms"
                                                  isSelected={
                                                    email_subscribedToInternalComms
                                                  }
                                                  onClick={this.updateValue}
                                                  isValid={true}
                                                />
                                                <Checkbox
                                                  label="I would like to receive occasional offers from Partners"
                                                  forLabel="optedInForMarketing"
                                                  isSelected={
                                                    optedInForMarketing
                                                  }
                                                  onClick={this.updateValue}
                                                  isValid={true}
                                                />
                                              </div>
                                              <div className="btn-left">
                                                <Cta
                                                  tag="button"
                                                  type="submit"
                                                  text="Next -> Finish or Upgrade"
                                                />
                                              </div>
                                            </Form>
                                          </div>
                                        )}
                                      </PlacesAutocomplete>
                                    )}
                                  </SignUpSect>
                                )}
                                {page === 4 && role === ROLE.cofounder && (
                                  <>
                                    {!this.state.isPlanSelected && (
                                      <div className="free-account-card">
                                        <div className="card-coner">
                                          <svg
                                            width="134"
                                            height="66"
                                            viewBox="0 0 134 66"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M126.138 -3.74942e-06H7.60277C6.56896 3.12514 6.00006 6.33265 6.00006 9.61161C6.00006 40.7583 -18.965 46.0002 41 46.0002C47.6348 46.0002 127.695 65.6743 134 65.0821V4.083C134 1.82782 130.482 -3.74942e-06 126.138 -3.74942e-06Z"
                                              fill="white"
                                            />
                                          </svg>
                                          <span>BASIC</span>
                                        </div>
                                        <Cta
                                          onClick={this.updatePage}
                                          tag="button"
                                          type="submit"
                                          text="Continue with Free Account"
                                        />
                                        <div className="forver">
                                          Forever Free
                                        </div>
                                        <ul>
                                          <li>
                                            <span>New Conversation Daily</span>{" "}
                                            <span>1</span>
                                          </li>
                                          <li>
                                            <span>Basic Filtering</span>{" "}
                                            <span>
                                              <svg
                                                width="13"
                                                height="14"
                                                viewBox="0 0 13 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M13 7.25658C13 10.8623 10.0912 13.7858 6.5 13.7858C2.91037 13.7858 0 10.8623 0 7.25658C0 3.65084 2.91037 0.727398 6.5 0.727398C10.0912 0.727398 13 3.65084 13 7.25658Z"
                                                  fill="#596D93"
                                                />
                                                <path
                                                  d="M5.33957 10.0849L3.53418 8.24368L3.53581 7.99883L4.48482 7.0619L4.72857 7.06353L5.34769 7.69688H5.59306L8.27755 4.99869H8.5213L9.46218 5.94539V6.19024L5.58332 10.0865L5.33957 10.0849Z"
                                                  fill="white"
                                                />
                                              </svg>
                                            </span>
                                          </li>
                                          <li>
                                            <span>Basic Filtering</span>{" "}
                                            <span>
                                              <svg
                                                width="13"
                                                height="14"
                                                viewBox="0 0 13 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M13 7.25658C13 10.8623 10.0912 13.7858 6.5 13.7858C2.91037 13.7858 0 10.8623 0 7.25658C0 3.65084 2.91037 0.727398 6.5 0.727398C10.0912 0.727398 13 3.65084 13 7.25658Z"
                                                  fill="#596D93"
                                                />
                                                <path
                                                  d="M5.33957 10.0849L3.53418 8.24368L3.53581 7.99883L4.48482 7.0619L4.72857 7.06353L5.34769 7.69688H5.59306L8.27755 4.99869H8.5213L9.46218 5.94539V6.19024L5.58332 10.0865L5.33957 10.0849Z"
                                                  fill="white"
                                                />
                                              </svg>
                                            </span>
                                          </li>
                                        </ul>
                                        <div className="bottom-conter">
                                          <svg
                                            width="67"
                                            height="51"
                                            viewBox="0 0 67 51"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M0 0.988232V45.0304C0 48.1725 2.53533 50.7198 5.664 50.7198H66.6427C55.9812 23.308 30.4489 3.39901 0 0.988232Z"
                                              fill="#596D93"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    )}
                                    <div>
                                      <UpgradeDialog me={{}} returnPath="/" inline />
                                    </div>
                                  </>
                                )}
                                {page === 5 && role === ROLE.cofounder && (
                                  <div>
                                    <SuccessCard apolloConsumer={client} />
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {page === 1 && role === ROLE.vendor && (
                                  <ApolloConsumer>
                                    {(client) => (
                                      <Form
                                        onSubmit={() =>
                                          this.pageValidationChecks(
                                            page,
                                            client
                                          )
                                        }
                                        method="post"
                                        loading={checkingEmailLoading}
                                        autoCompleteDisabled
                                      >
                                        <Error error={error} />
                                        <SignUpSect>
                                          <div className="sign-up-form__logo">
                                            <img
                                              src="/static/logo.svg"
                                              alt="StartHawk"
                                            />
                                          </div>
                                          <div className="heading_section cofounder_div">
                                            <Heading
                                              copy="Join as a Vendor or Resource"
                                              element="h3"
                                            />
                                            <a
                                              className="header_a_button cofounder"
                                              onClick={() =>
                                                this.setState({
                                                  role: ROLE.cofounder,
                                                })
                                              }
                                            >
                                              Sign Up as a <br></br> Co-Founder
                                            </a>
                                          </div>

                                          <Error error={error} />
                                          <TextInput
                                            label="Company Name"
                                            forLabel="companyName"
                                            type="text"
                                            updateValue={this.updateValue}
                                            value={companyName}
                                            isValid={companyNameValid}
                                            validationErrorMsg="Please provide a valid company name using English characters"
                                          />
                                          <TextInput
                                            label="User First Name"
                                            forLabel="firstName"
                                            type="text"
                                            updateValue={this.updateValue}
                                            value={firstName}
                                            isValid={firstNameValid}
                                            validationErrorMsg="Please provide a valid user first name using English characters"
                                          />
                                          <TextInput
                                            label="Email"
                                            forLabel="email"
                                            type="text"
                                            updateValue={this.updateValue}
                                            value={email}
                                            isValid={emailValid}
                                            validationErrorMsg="Please provide a valid email"
                                            emailUnique={emailUnique}
                                          />
                                          <TextInput
                                            label="Password"
                                            forLabel="password"
                                            type="password"
                                            updateValue={this.updateValue}
                                            value={password}
                                            isValid={passwordValid}
                                            validationErrorMsg="Please provide a password with a minimum length of 8 characters, at least one letter and at least one number"
                                          />
                                          <Checkbox
                                            label="I agree to the privacy policy and terms of service"
                                            supportedLinks={[
                                              {
                                                supportedLinkText:
                                                  "Read our privacy policy",
                                                supportedLink:
                                                  "/privacy-policy",
                                              },
                                              {
                                                supportedLinkText:
                                                  "Read our terms of service",
                                                supportedLink:
                                                  "/terms-of-service",
                                              },
                                            ]}
                                            forLabel="agreedToGdpr"
                                            isSelected={agreedToGdpr}
                                            onClick={this.updateValue}
                                            hasValidation
                                            isValid={agreedToGdprValid}
                                            validationErrorMsg="Please accept our privacy policy and terms of service"
                                          />
                                          <Cta
                                            tag="button"
                                            type="submit"
                                            text="Join the Community"
                                          />
                                        </SignUpSect>
                                      </Form>
                                    )}
                                  </ApolloConsumer>
                                )}
                                {page == 2 && role === ROLE.vendor && (
                                  <ApolloConsumer>
                                    {(client) => (
                                      <Form
                                        onSubmit={() =>
                                          this.pageValidationChecks(
                                            page,
                                            client
                                          )
                                        }
                                        method="post"
                                        loading={checkingOtpLoading}
                                        autoCompleteDisabled
                                      >
                                        <Error error={error} />
                                        <SignUpSect>
                                          {" "}
                                          <div className="otp_screen_icon">
                                            <img
                                              src="/static/images/mailIcon.svg"
                                              alt="StartHawk"
                                            />
                                          </div>
                                          <div className="otp_heading">
                                            <h1>Please verify your email</h1>
                                          </div>
                                          <div className="otp_mail_text">
                                            <p>
                                              A code has been sent to your email
                                              address:
                                              <span>
                                                {email}
                                              </span> <br></br> Please enter
                                              that code here
                                            </p>
                                          </div>
                                          <Error error={error} />
                                          <div className="otp_screen_input">
                                            <TextInput
                                              label="Enter the 6-digit code here"
                                              forLabel="otp"
                                              type="text"
                                              updateValue={this.updateValue}
                                              value={otp}
                                              isValid={otpValid}
                                              maxLength={6}
                                              validationErrorMsg="incorrect Otp or enter valid otp"
                                            />
                                          </div>
                                          <div className="otp_section_note">
                                            <p>
                                              Didn’t receive the email ?
                                              <span
                                                onClick={() =>
                                                  this.sendOtpAgain(client)
                                                }
                                                className="try_again_text"
                                              >
                                                Try Again
                                              </span>
                                              <br></br>
                                              Check spam/junk folders if you do
                                              not see the email
                                            </p>
                                          </div>
                                          {/* <a
                                className="header__menu__item__a"
                                onClick={() => this.sendOtpAgain(client)}
                              >
                                Try Again
                              </a> */}
                                          <Cta
                                            tag="button"
                                            type="submit"
                                            text="Next"
                                          />
                                        </SignUpSect>
                                      </Form>
                                    )}
                                  </ApolloConsumer>
                                )}
                                {page == 3 && role === ROLE.vendor && (
                                  <SignUpSect>
                                    <Form
                                      onSubmit={() =>
                                        this.pageValidationChecks(3)
                                      }
                                      method="post"
                                      loading={loading}
                                    >
                                      {" "}
                                      <div className="signup_screens_icons">
                                        <img
                                          src="/static/images/msgIcon.svg"
                                          alt="StartHawk"
                                        />
                                      </div>
                                      <div className="signup_screen_heading">
                                        <h1>Communication Preferences</h1>
                                      </div>
                                      <div className="signup_screen_text">
                                        <p>
                                          Communication is pretty important for
                                          our network to operate. The easier we
                                          communicate, the easier co-founder
                                          connections can be facilitated.
                                        </p>
                                      </div>
                                      <div className="list_checkbox">
                                        <Checkbox
                                          label="Send me an email when someone messages me on FounderCloud"
                                          forLabel="email_subscribedToCommunityAnswers"
                                          isSelected={
                                            email_subscribedToCommunityAnswers
                                          }
                                          onClick={this.updateValue}
                                          isValid={true}
                                        />
                                        <Checkbox
                                          label="I would like to receive the weekly FounderCloud update newsletter with news and community activity information"
                                          forLabel="email_subscribedToInternalComms"
                                          isSelected={
                                            email_subscribedToInternalComms
                                          }
                                          onClick={this.updateValue}
                                          isValid={true}
                                        />
                                        <Checkbox
                                          label="I would like to receive occasional offers from Partners"
                                          forLabel="optedInForMarketing"
                                          isSelected={optedInForMarketing}
                                          onClick={this.updateValue}
                                          isValid={true}
                                        />
                                      </div>
                                      <Cta
                                        tag="button"
                                        type="submit"
                                        text="Next"
                                      />
                                    </Form>
                                  </SignUpSect>
                                )}
                                {page === 4 && role === ROLE.vendor && (
                                  <SignUpSect>
                                    <Form
                                      onSubmit={() =>
                                        this.pageValidationChecks(4)
                                      }
                                      method="post"
                                      loading={loading}
                                    >
                                      <h2>
                                        Please provide some more info on your
                                        company or services
                                      </h2>
                                      <TextArea
                                        value={shortDescription}
                                        forLabel="shortDescription"
                                        updateValue={this.updateValue}
                                        placeholder="Type short description about your company"
                                        isValid={shortDescriptionValid}
                                        validationErrorMsg="Please tell us about your organization in short description in 20 words or more (200 character max)"
                                        maxLength={200}
                                      />
                                      <TextArea
                                        value={longDescription}
                                        forLabel="longDescription"
                                        updateValue={this.updateValue}
                                        placeholder="Type long description about your company"
                                        isValid={longDescriptionValid}
                                        validationErrorMsg="Please tell us about your organization in long description in 50 words or more (500 character max)"
                                        maxLength={500}
                                      />
                                      <SelectDropdown
                                        options={industriesData}
                                        label="Which industry/ business type best describes your company?"
                                        forLabel="ideaIndustry"
                                        selectedOptionId={ideaIndustry}
                                        onClick={this.updateValue}
                                        isValid={ideaIndustryValid}
                                        validationErrorMsg="Please select an industry/ business type for your company"
                                        resetDefaultState
                                      />
                                      <SelectDropdown
                                        options={capabilitiesData}
                                        label="Which capability type best describes your company?"
                                        forLabel="capability"
                                        selectedOptionId={capability}
                                        onClick={this.updateValue}
                                        isValid={capabilityValid}
                                        validationErrorMsg="Please select a capability type for your company"
                                        resetDefaultState
                                      />
                                      <Cta
                                        tag="button"
                                        type="submit"
                                        text="Next"
                                      />
                                    </Form>
                                  </SignUpSect>
                                )}
                                {page === 5 && role === ROLE.vendor && (
                                  <SignUpSect>
                                    {googleMapAPILoaded && (
                                      <PlacesAutocomplete
                                        value={location}
                                        searchOptions={{ types: ["(cities)"] }}
                                        onChange={this.handleLocationChange}
                                        onSelect={this.handleLocationSelect}
                                        highlightFirstSuggestion
                                      >
                                        {({
                                          getInputProps,
                                          suggestions,
                                          getSuggestionItemProps,
                                          placesLoading,
                                        }) => (
                                          <>
                                            <Form
                                              onSubmit={() =>
                                                this.pageValidationChecks(
                                                  5,
                                                  null,
                                                  signUp
                                                )
                                              }
                                              method="post"
                                              loading={
                                                locationLoading || loading
                                              }
                                            >
                                              <h2>
                                                Please tell us a little more
                                                info
                                              </h2>
                                              <Error error={error} />
                                              <TextInput
                                                label="City/ town where you're based"
                                                forLabel="location"
                                                type="text"
                                                updateValue={this.updateValue}
                                                modifierClass="location"
                                                additionalInputProps={getInputProps()}
                                                hasSpinner
                                                loading={placesLoading}
                                                value={location}
                                                selectedOption={cityLabel}
                                                removeSelectedOption={
                                                  this.removeSelectedCity
                                                }
                                                isValid={locationValid}
                                                validationErrorMsg="Please tell us where you're based (select an option from the options that appear after typing)"
                                              />
                                              <div className="location-auto">
                                                {suggestions.length > 0 && (
                                                  <div className="location-auto__inner">
                                                    {suggestions.map(
                                                      (suggestion) => {
                                                        return (
                                                          <LocationItem
                                                            key={
                                                              suggestion.place_id ||
                                                              suggestion.placeId
                                                            }
                                                            getSuggestionItemProps={
                                                              getSuggestionItemProps
                                                            }
                                                            className={
                                                              suggestion.active
                                                                ? "location-auto__suggestion location-auto__suggestion--active"
                                                                : "location-auto__suggestion"
                                                            }
                                                            suggestion={
                                                              suggestion
                                                            }
                                                          />
                                                        )
                                                      }
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              <Heading
                                                copy="Select an image to upload for your profile picture"
                                                element="h3"
                                              />
                                              <ImageUpload
                                                imageErrorMessage={
                                                  imageErrorMessage
                                                }
                                                profilePic={profilePic}
                                                profilePicLastModified={
                                                  profilePicLastModified
                                                }
                                                updateImage={this.updateImage}
                                                imgSizeX={imgSizeX}
                                                imgSizeY={imgSizeY}
                                                controlledPositionX={
                                                  controlledPositionX
                                                }
                                                controlledPositionY={
                                                  controlledPositionY
                                                }
                                                ratio={ratio}
                                              />
                                              <Cta
                                                tag="button"
                                                type="submit"
                                                text="Finish registration"
                                                disabled={loading}
                                              />
                                            </Form>
                                          </>
                                        )}
                                      </PlacesAutocomplete>
                                    )}
                                  </SignUpSect>
                                )}
                              </>
                            )
                          }}
                        </Mutation>
                      )}
                    </ApolloConsumer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default Signup
